/** Locale-aware formatting helpers. */

const NUM = new Intl.NumberFormat('bg-BG');
const NUM_COMPACT = new Intl.NumberFormat('bg-BG', {
  notation: 'compact', maximumFractionDigits: 1,
});

type Maybe<T> = T | null | undefined;

export function num(value: Maybe<number>): string {
  return value == null ? '—' : NUM.format(Math.round(value));
}

export function compact(value: Maybe<number>): string {
  return value == null ? '—' : NUM_COMPACT.format(value);
}

const ONE_DECIMAL = new Intl.NumberFormat('bg-BG', { maximumFractionDigits: 1 });

/**
 * Money the way a column is read: floored to one decimal of хил./млн./млрд.
 *
 * Truncated, not rounded — "1,99 млрд." shown as "2,0 млрд." would print a
 * larger number than was ever published, and this site's one promise is
 * that no figure is inflated. The cost is a display that can understate by
 * up to a tenth of a unit, which is what the exact figure in the hover
 * title (see <Amount/>) is for.
 */
export function money(value: Maybe<number>, currency: Maybe<string> = 'BGN'): string {
  if (value == null) return '—';
  const unit = !currency || currency === 'BGN' ? 'лв.' : currency;
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);
  const banded = (div: number, band: string) =>
    `${sign}${ONE_DECIMAL.format(Math.floor((abs / div) * 10) / 10)} ${band} ${unit}`;
  if (abs >= 1e9) return banded(1e9, 'млрд.');
  if (abs >= 1e6) return banded(1e6, 'млн.');
  if (abs >= 1e3) return banded(1e3, 'хил.');
  return `${sign}${NUM.format(Math.floor(abs))} ${unit}`;
}

/** Every digit, for the hover title and anywhere precision is the point. */
export function moneyExact(value: Maybe<number>, currency: Maybe<string> = 'BGN'): string {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency || 'BGN',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // an unrecognised currency code must not take the page down with it
    return `${NUM.format(value)} ${currency ?? ''}`.trim();
  }
}

/** Официален фиксиран курс на валутния борд (не пазарен). */
export const BGN_PER_EUR = 1.95583;

/**
 * Normalizes an amount to BGN so values from different sources can be
 * summed. Sources mix currencies — TED reports EUR, ЦАИС ЕОП reports EUR
 * or BGN — and adding them unconverted produces a meaningless number. The
 * peg is a fixed legal rate, so this conversion is exact. An unknown
 * currency is returned untouched rather than guessed at.
 */
export function toBGN(amount: Maybe<number>, currency: Maybe<string>): number {
  if (amount == null) return 0;
  if (!currency || currency === 'BGN') return amount;
  if (currency === 'EUR') return amount * BGN_PER_EUR;
  return amount;
}

export function sumBGN<T>(
  items: readonly T[],
  amountOf: (item: T) => Maybe<number>,
  currencyOf: (item: T) => Maybe<string>,
): number {
  return items.reduce((total, item) => total + toBGN(amountOf(item), currencyOf(item)), 0);
}

export function date(value: Maybe<string>): string {
  if (!value) return '—';
  const iso = String(value).slice(0, 10);
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString('bg-BG', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * A moment, not a day.
 *
 * `date` deliberately drops the time — for a mandate or an award the hour
 * is noise. For anything recomputed on a schedule it is the whole point:
 * the signals are rebuilt hourly, and a date alone makes the freshest run
 * and a day-old one look identical.
 *
 * Read in the reader's own timezone, since the question it answers is
 * "how long ago", not "at what UTC instant".
 */
export function dateTime(value: Maybe<string>): string {
  if (!value) return '—';
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return date(value);
  return parsed.toLocaleString('bg-BG', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function truncate(text: Maybe<string>, max = 90): string {
  const value = String(text ?? '');
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

export function shortHash(hash: Maybe<string>): string {
  return hash ? `${String(hash).slice(0, 10)}…` : '—';
}

export interface HoldingProps {
  share_percent?: Maybe<number>;
  share_amount?: Maybe<number>;
  share_currency?: Maybe<string>;
}

/** Capital holding as filed: an amount, or a percentage when known. */
export function holding(props: Maybe<HoldingProps>): string {
  if (!props) return '';
  if (props.share_percent != null) return `${props.share_percent}%`;
  if (props.share_amount != null) {
    return `${NUM.format(props.share_amount)} ${props.share_currency || ''}`.trim();
  }
  return '—';
}

export interface ValidityProps {
  valid_from?: Maybe<string>;
  valid_to?: Maybe<string>;
}

/** "от 30.10.2023" / "30.10.2023 – 12.05.2025" / "—" */
export function validity(props: Maybe<ValidityProps>): string {
  const from = props?.valid_from && props.valid_from !== 'unknown'
    ? date(props.valid_from) : null;
  const to = props?.valid_to ? date(props.valid_to) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return `от ${from}`;
  if (to) return `до ${to}`;
  return '—';
}

export interface MandateProps {
  elected_year?: Maybe<number>;
  term_end?: Maybe<number>;
  elected_round?: Maybe<number>;
}

/** "2015 – 2019" for a closed mandate, "от 2023" for the sitting one. */
export function mandateTerm(properties: Maybe<MandateProps>): string {
  const from = properties?.elected_year;
  if (from == null) return '—';
  return properties?.term_end ? `${from} – ${properties.term_end}` : `от ${from}`;
}

/**
 * The party that nominated a mayor for one particular mandate.
 *
 * A mayor re-elected under a different banner has one NOMINATED_BY edge
 * per election, so the nomination has to be matched to the mandate's year;
 * taking the first would name the wrong party for anyone who switched.
 */
export function nominationFor<TParty>(mandate: Maybe<{
  properties?: Maybe<{ elected_year?: Maybe<number> }>;
  node?: Maybe<{
    nominatedByConnection?: Maybe<{
      edges: readonly { properties: { election_year?: Maybe<number> }; node: TParty }[];
    }>;
  }>;
}>): TParty | null {
  const year = mandate?.properties?.elected_year;
  const nominations = mandate?.node?.nominatedByConnection?.edges ?? [];
  const match = nominations.find((entry) => entry.properties?.election_year === year);
  return (match ?? nominations[0])?.node ?? null;
}
